// react
import { useMemo } from 'react';

// types
import { Pagination, Transaction } from '../../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// mui
import { Grid } from '@mui/material';

// cards
import { CardAdvance } from '../../../cards/CardAdvance/CardAdvance';
import { CardEmpty } from '../../../cards/CardEmpty/CardEmpty';

// fields
import { SelectTransactionPage } from '../../../fields/SelectTransactionPage/SelectTransactionPage';

// forms
import { FormSearch } from '../../../forms/FormSearch/FormSearch';

// ui
import { EmptyProps } from '../../../ui/Empty/Empty';
import { TableFooter } from '../../../ui/TableFooter/TableFooter';
import { TableHeaderProps } from '../../../ui/TableHeaders/TableHeader/TableHeader';
import { TableHeaders } from '../../../ui/TableHeaders/TableHeaders';

// props
export interface TableAdvancesProps {
	hasSearch?: boolean;
	hasSelectPage?: boolean;
	isLoading?: boolean;
	pagination: Pagination;
	refetchTransactions: () => void;
	transactions: Transaction[];
}

export const TableAdvances = ({
	hasSearch = true,
	hasSelectPage = true,
	isLoading = false,
	pagination,
	refetchTransactions,
	transactions,
}: TableAdvancesProps) => {
	// hooks
	const { t } = useTranslation();

	// headers
	const headers: TableHeaderProps[] = [
		{
			GridProps: {
				lg: 3,
			},
			label: t('date'),
			value: 'date',
		},
		{
			GridProps: {
				lg: 3,
			},
			label: t('toAccount'),
			value: 'toAccount',
		},
		{
			GridProps: {
				lg: 2,
			},
			label: t('amount'),
			value: 'amount',
		},
		{
			GridProps: {
				lg: 3,
			},
			label: t('updated'),
			value: 'modified',
		},
		{
			GridProps: {
				lg: true,
			},
			label: '',
			value: 'actions',
		},
	];

	// empty
	const empty = useMemo(() => {
		const emptyProps: EmptyProps = {
			content: { children: t('empty.advances.content') },
			IconCircleProps: {
				color: 'grey',
				type: 'advance',
			},
			title: { children: t('empty.advances.title') },
		};

		return emptyProps;
	}, [t]);

	// content
	const content = useMemo(() => {
		if (isLoading || !transactions.length) {
			return <CardEmpty empty={empty} isLoading={isLoading} />;
		}

		return (
			<Grid container spacing={2}>
				{transactions.map((transaction) => (
					<Grid item key={transaction.id} xs={12}>
						<CardAdvance refetchTransactions={refetchTransactions} transaction={transaction} />
					</Grid>
				))}
			</Grid>
		);
	}, [empty, isLoading, refetchTransactions, transactions]);

	return (
		<Grid container spacing={2}>
			{hasSelectPage && (
				<Grid item xs={12} md={3} lg={2}>
					<SelectTransactionPage value="advances" />
				</Grid>
			)}
			{hasSearch && (
				<Grid item xs={12} md={true}>
					<FormSearch InputProps={{ placeholder: t('searchById') }} />
				</Grid>
			)}
			<Grid item xs={12} sx={{ display: { xs: 'none', lg: 'block' } }}>
				<TableHeaders headers={headers} />
			</Grid>
			<Grid item xs={12}>
				{content}
			</Grid>
			<Grid item xs={12}>
				<TableFooter numShowing={transactions.length} pagination={pagination} />
			</Grid>
		</Grid>
	);
};
